import { memo, useCallback } from "react";

// Helpers
import { classNames, useTranslate } from "helpers";

// Servisni sloj
import {
  FLEET_VEHICLE_STATUS_CONFIG,
  ROOT_STATUSES,
} from "services/api/fleet/vehicles/consts";
import { FleetVehicleStatusEnum } from "services/api/fleet/vehicles/types";

import { VehicleStatusPickerProps } from "./types";

/**
 * Naziv pod-statusa nosi i roditelja ("Aktivno - U najmu"), jer isti prijevod ide i u
 * značku i u tablice. U popisu se roditelj podrazumijeva iz uvlake, pa razlika ide u
 * zagradu i podeblja se — ona je ono što se ovdje bira.
 */
const renderSubStatusLabel = (label: string) => {
  const [parent, ...rest] = label.split(/\s+[-–]\s+/);

  if (rest.length === 0) return label;

  return (
    <>
      {parent} (
      <span className="font-bold uppercase tracking-wide">{rest.join(" – ")}</span>)
    </>
  );
};

/**
 * Prvi korak promjene statusa — popis statusa s pod-statusima uvučenima ispod roditelja.
 * Samo odabir; što status traži dalje, zna čarobnjak koji ga prikazuje.
 */
const VehicleStatusPicker: React.FC<VehicleStatusPickerProps> = (props) => {
  const { value, onChange } = props;

  const { t } = useTranslate();

  const handleStatusClick = useCallback(
    (status: FleetVehicleStatusEnum) => () => {
      onChange(status);
    },
    [onChange]
  );

  const renderStatusItem = (status: FleetVehicleStatusEnum, isSubStatus = false) => {
    const config = FLEET_VEHICLE_STATUS_CONFIG[status];
    const isSelected = value === status;

    return (
      <button
        key={status}
        type="button"
        onClick={handleStatusClick(status)}
        className={classNames(
          "w-full flex items-center gap-3 px-4 py-3 text-left transition-all rounded-sm",
          isSubStatus ? "pl-12 " : "",
          isSelected ? "bg-blue-50 ring-2 ring-blue-500" : "hover:bg-slate-50"
        )}
      >
        <span className={classNames("w-3 h-3 rounded-full flex-shrink-0", config.dotColor)} />
        <span className="text-sm font-medium text-slate-700">
          {isSubStatus ? renderSubStatusLabel(t(config.label)) : t(config.label)}
        </span>

        {isSelected && (
          <span className="ml-auto text-blue-500">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="flex flex-col gap-1 py-6 px-4">
      {ROOT_STATUSES.map((status) => {
        const config = FLEET_VEHICLE_STATUS_CONFIG[status];

        return (
          <div key={status}>
            {renderStatusItem(status)}
            {config.subStatuses && config.subStatuses.length > 0 && (
              <div className="flex flex-col gap-1 mt-1">
                {config.subStatuses.map((subStatus) => renderStatusItem(subStatus, true))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default memo(VehicleStatusPicker);
